import { Link, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AlertTriangle, ArrowUpDown, MessageSquare, ReceiptText } from 'lucide-react'
import { AP_INVOICE_STATUSES } from '../constants'
import { useUpdateInvoice } from '../mutations'
import type { ApInvoiceListItem, PaginatedApInvoices } from '../view-models'
import { formatInvoiceStatus, getInvoiceStatusStyle, paymentPartLabel } from '../status'
import { ChoicePicker } from '@/components/choice-picker'
import { StatusPill } from '@/components/status-pill'
import type { StatusTone } from '@/lib/status-tones'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatMoney, formatDateOnly, formatStatus } from '@/lib/utils'
import { BulkSelectCheckbox } from './bulk-select-checkbox'
import { getApiErrorMessage } from '@/lib/api-error'

// List payment/link/terms fallbacks intentionally differ from invoice detail.
function paymentDueDate(invoice: ApInvoiceListItem) {
  // A split or COD part without a ship/delivery date yet is not due: no fallback date.
  if (invoice.payment_part) return invoice.payment_part.due_date
  return (
    invoice.payment_urgency_date ||
    invoice.effective_due_date ||
    invoice.priority_due_date ||
    invoice.official_due_date
  )
}

function paymentUrgencyLabel(invoice: ApInvoiceListItem) {
  if (invoice.payment_part) {
    return paymentPartLabel(invoice.payment_part, invoice.payment_urgency === 'overdue')
  }
  if (!invoice.payment_urgency) return 'Due'
  return {
    release_blocking: 'Pay to release',
    early_pay: 'Early pay',
    pride: 'Pride deadline',
    due: 'Due',
    overdue: 'Overdue',
  }[invoice.payment_urgency]
}

function isUnpaid(invoice: ApInvoiceListItem) {
  return invoice.status !== 'paid'
}

function hasMeaningfulPoDifference(invoice: ApInvoiceListItem) {
  const difference = Number(invoice.po_difference ?? 0)
  return (
    invoice.po_match !== 'matched' && Number.isFinite(difference) && Math.abs(difference) > 0.005
  )
}

function poDifferenceClass(invoice: ApInvoiceListItem) {
  if (!hasMeaningfulPoDifference(invoice)) return ''
  const difference = Math.abs(Number(invoice.po_difference ?? 0))
  return difference >= 100
    ? 'border-red-200 bg-red-50 text-red-700'
    : 'border-amber-200 bg-amber-50 text-amber-700'
}

function urgencyTone(invoice: ApInvoiceListItem): StatusTone {
  if (!isUnpaid(invoice) || !invoice.payment_urgency) return 'neutral'
  if (invoice.payment_urgency === 'overdue') return 'danger'
  if (invoice.payment_urgency === 'release_blocking') return 'warning'
  if (invoice.payment_urgency === 'early_pay' || invoice.due_soon) return 'progress'
  return 'neutral'
}

/** What is owed next and when: a colored cue for unpaid, payment-ready invoices. */
function DueCue({ invoice }: { invoice: ApInvoiceListItem }) {
  const date = paymentDueDate(invoice)
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <StatusPill tone={urgencyTone(invoice)}>{paymentUrgencyLabel(invoice)}</StatusPill>
      <span className="text-sm font-medium tabular-nums">
        {!date && invoice.payment_part ? 'Date not set' : formatDateOnly(date)}
      </span>
    </div>
  )
}

const STATUS_CHOICES = AP_INVOICE_STATUSES.map((invoiceStatus) => ({
  value: invoiceStatus,
  label: formatInvoiceStatus(invoiceStatus),
}))

function InvoiceStatusControl({ invoice }: { invoice: ApInvoiceListItem }) {
  const updateInvoice = useUpdateInvoice(invoice.uuid ?? '')
  const status = invoice.status || 'needs_review'

  return (
    <ChoicePicker
      title="Invoice status"
      label={`Status: ${formatInvoiceStatus(status)}. Change status for ${invoice.invoice_number || invoice.uuid}`}
      value={status}
      choices={STATUS_CHOICES}
      pending={updateInvoice.isPending}
      disabled={!invoice.uuid}
      onSelect={(statusValue) => {
        if (!invoice.uuid) return
        updateInvoice.mutate(
          { status: statusValue },
          {
            onSuccess: () =>
              toast.success(`Status updated for ${invoice.invoice_number || 'invoice'}`),
            onError: (err) =>
              toast.error(getApiErrorMessage(err, 'Status update failed')),
          },
        )
      }}
    >
      <span
        className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium"
        style={getInvoiceStatusStyle(status)}
      >
        {formatInvoiceStatus(status)}
      </span>
    </ChoicePicker>
  )
}

function DuplicateFlag({ invoice }: { invoice: ApInvoiceListItem }) {
  if (!invoice.duplicate_status || invoice.duplicate_status === 'unique') return null
  return (
    <StatusPill tone="danger" className="gap-1">
      <AlertTriangle aria-hidden className="h-3 w-3" />
      {formatInvoiceStatus(invoice.duplicate_status)}
    </StatusPill>
  )
}

function invoiceVendor(invoice: ApInvoiceListItem): string {
  return invoice.vendor?.name || invoice.vendor_raw || 'Unlinked vendor'
}

function primaryPo(invoice: ApInvoiceListItem) {
  return invoice.po_links?.[0]
}

function invoiceProjectName(invoice: ApInvoiceListItem) {
  return (
    invoice.internal_project?.name ||
    primaryPo(invoice)?.project?.name ||
    invoice.project_ship_to_raw ||
    'Project pending'
  )
}

function invoiceUnlinkedProjectContext(invoice: ApInvoiceListItem) {
  return (
    invoice.project_ship_to_raw ||
    invoice.internal_project?.name ||
    primaryPo(invoice)?.project?.name ||
    null
  )
}

function invoiceProjectLabel(invoice: ApInvoiceListItem) {
  const projectUuid = invoiceProjectUuid(invoice)
  if (projectUuid) return invoiceProjectName(invoice)
  const context = invoiceUnlinkedProjectContext(invoice)
  return context ? `unlinked project: ${context}` : 'unlinked project'
}

function invoiceProjectUuid(invoice: ApInvoiceListItem) {
  return invoice.internal_project?.uuid || primaryPo(invoice)?.project?.uuid
}

function invoicePoUuid(po: ReturnType<typeof primaryPo>) {
  return po?.purchase_order?.uuid || po?.purchase_order_uuid
}

function invoicePoNumber(po: ReturnType<typeof primaryPo>) {
  return po?.purchase_order?.po_number || po?.po_number_raw
}

function formatInvoiceTerms(invoice: ApInvoiceListItem) {
  const terms = invoice.payment_terms
  if (terms?.category && terms.category !== 'unknown') {
    const parts: string[] = []
    if (terms.discount_percent != null && terms.discount_days != null) {
      parts.push(`${terms.discount_percent}% ${terms.discount_days}`)
    }
    if (terms.net_days != null) parts.push(`Net ${terms.net_days}`)
    if (parts.length) return parts.join(', ')
    return formatStatus(terms.category)
  }
  return terms?.raw_text || invoice.payment_terms_raw || 'Terms not set'
}

/** How it is being paid, only once something is set: "Scheduled · ACH" and a receipt mark. */
function PaymentProgress({ invoice }: { invoice: ApInvoiceListItem }) {
  const payment = invoice.payment_summary
  const facts = [
    payment?.status && payment.status !== 'unpaid' ? formatStatus(payment.status) : null,
    payment?.method,
  ].filter(Boolean)
  if (!facts.length && !payment?.has_receipt) return null
  return (
    <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      <span className="truncate">{facts.join(' · ')}</span>
      {payment?.has_receipt && (
        <ReceiptText aria-label="Receipt on file" className="h-3.5 w-3.5 shrink-0 text-foreground" />
      )}
    </p>
  )
}

function InvoiceLinks({ invoice }: { invoice: ApInvoiceListItem }) {
  const po = primaryPo(invoice)
  const poUuid = invoicePoUuid(po)
  const poNumber = invoicePoNumber(po)
  const projectUuid = invoiceProjectUuid(invoice)
  const shipmentLinks = invoice.shipment_links ?? []
  return (
    <div className="space-y-1 text-sm">
      {poNumber ? (
        poUuid ? (
          <Link
            to="/purchase-orders/$poId"
            params={{ poId: poUuid }}
            onClick={(event) => event.stopPropagation()}
            className="block truncate font-mono text-primary hover:underline"
          >
            {poNumber}
          </Link>
        ) : (
          <span className="block truncate font-mono">{poNumber}</span>
        )
      ) : null}
      {projectUuid ? (
        <Link
          to="/projects/$projectId"
          params={{ projectId: projectUuid }}
          onClick={(event) => event.stopPropagation()}
          className="block truncate text-primary hover:underline"
        >
          {invoiceProjectName(invoice)}
        </Link>
      ) : null}
      {shipmentLinks.map((shipment) => (
        <Link
          key={shipment.shipment_uuid}
          to="/shipments/$shipmentId"
          params={{ shipmentId: shipment.shipment_uuid }}
          onClick={(event) => event.stopPropagation()}
          className="block truncate text-primary hover:underline"
        >
          Shipment{shipment.po_number ? ` · ${shipment.po_number}` : ''}
        </Link>
      ))}
      {!projectUuid && shipmentLinks.length === 0 ? (
        <span className="block truncate text-xs text-muted-foreground">
          {invoiceProjectLabel(invoice)}
        </span>
      ) : null}
      {hasMeaningfulPoDifference(invoice) ? (
        <span
          className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${poDifferenceClass(invoice)}`}
        >
          Difference {formatMoney(invoice.po_difference)}
        </span>
      ) : null}
    </div>
  )
}

function hasAmount(amount: string | number | null | undefined) {
  return Math.abs(Number(amount ?? 0)) > 0.005
}

/** What is still owed, then only the amounts that explain it (total, deposit, discount, credits). */
function InvoiceOwed({ invoice }: { invoice: ApInvoiceListItem }) {
  const remaining = Number(invoice.remaining_balance ?? 0)
  const total = Number(invoice.invoice_total ?? 0)
  const settled = invoice.balance_status === 'settled'
  const lines = [
    Math.abs(total - remaining) > 0.005 ? `of ${formatMoney(invoice.invoice_total)}` : null,
    hasAmount(invoice.settled_amount) && !settled ? `${formatMoney(invoice.settled_amount)} paid` : null,
    hasAmount(invoice.applied_credit_amount)
      ? `${formatMoney(invoice.applied_credit_amount)} credit`
      : null,
  ].filter(Boolean)
  return (
    <div className="space-y-0.5 text-right tabular-nums">
      <p className="text-sm font-semibold">{settled ? 'Paid' : formatMoney(invoice.remaining_balance)}</p>
      {invoice.payment_part?.part === 'deposit' && (
        <p className="text-[11px] font-medium text-amber-800 dark:text-amber-200">
          Deposit {formatMoney(invoice.payment_part.amount)}
        </p>
      )}
      {invoice.early_payment_terms && (
        <p className="text-[11px] font-medium text-sky-800 dark:text-sky-200">
          Discounted {formatMoney(invoice.early_payment_terms.amount_to_remit)}
        </p>
      )}
      {lines.map((line) => (
        <p key={line} className="text-[11px] text-muted-foreground">
          {line}
        </p>
      ))}
    </div>
  )
}

const NOTE_TAG = /@\[([^\]]+)\]\((\w+):[^)]+\)\s?/g
/** Tags the note was left on (this invoice, its POs, its action items) go without saying in its row. */
const CONTEXT_TAGS = new Set(['ap_invoice', 'purchase_order', 'action_item'])

function noteText(content: string) {
  return content
    .replace(NOTE_TAG, (_match, display: string, type: string) => (CONTEXT_TAGS.has(type) ? '' : `@${display} `))
    .trim()
}

/** The newest note staff left on the invoice, so Accounting sees it from the list (USA-1180). */
function InvoiceLatestNote({ invoice }: { invoice: ApInvoiceListItem }) {
  const notes = invoice.comments_summary
  if (!notes) return null
  const text = noteText(notes.latest_content)
  const more = notes.count > 1 ? ` (+${notes.count - 1} more)` : ''
  return (
    <p className="flex min-w-0 items-start gap-1.5 text-xs" title={`${notes.latest_author}: ${text}${more}`}>
      <MessageSquare className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" aria-label="Newest note" />
      <span className="line-clamp-2 min-w-0 whitespace-normal break-words">
        <span className="font-medium">{notes.latest_author}:</span> {text}
        {more && <span className="text-muted-foreground">{more}</span>}
      </span>
    </p>
  )
}

function InvoiceIdentity({ invoice }: { invoice: ApInvoiceListItem }) {
  const number = invoice.invoice_number || 'Missing invoice #'
  return (
    <div className="min-w-0 space-y-0.5">
      <div className="flex min-w-0 items-center gap-2">
        <Link
          to="/invoices/$invoiceId"
          params={{ invoiceId: invoice.uuid! }}
          onClick={(event) => event.stopPropagation()}
          className="truncate font-mono text-sm font-medium text-primary hover:underline"
        >
          {number}
        </Link>
        <DuplicateFlag invoice={invoice} />
      </div>
      <p className="truncate text-xs font-medium">{invoiceVendor(invoice)}</p>
      <div className="flex min-w-0 items-center gap-1.5">
        <InvoiceStatusControl invoice={invoice} />
        <span className="truncate text-xs text-muted-foreground">{formatInvoiceTerms(invoice)}</span>
      </div>
      <InvoiceLatestNote invoice={invoice} />
    </div>
  )
}

export function InvoiceListResults({
  data,
  isLoading,
  toggleSort,
  selectedInvoiceUuids,
  allVisibleSelected,
  setVisibleSelection,
  setRowSelection,
}: {
  data: PaginatedApInvoices | undefined
  isLoading: boolean
  toggleSort: (by: string) => void
  selectedInvoiceUuids: string[]
  allVisibleSelected: boolean
  setVisibleSelection: (checked: boolean) => void
  setRowSelection: (uuid: string | null | undefined, checked: boolean) => void
}) {
  const navigate = useNavigate()
  const openInvoice = (invoice: ApInvoiceListItem) =>
    navigate({ to: '/invoices/$invoiceId', params: { invoiceId: invoice.uuid! } })
  const isSelected = (invoice: ApInvoiceListItem) =>
    Boolean(invoice.uuid && selectedInvoiceUuids.includes(invoice.uuid))

  return (
    <>
      <div className="hidden min-w-0 lg:block">
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <BulkSelectCheckbox
                  label="Select all visible invoices"
                  checked={allVisibleSelected}
                  onChange={setVisibleSelection}
                />
              </TableHead>
              <TableHead className="w-[30%]">
                <Button variant="ghost" size="sm" className="-ml-3" onClick={() => toggleSort('invoice_number')}>
                  Invoice <ArrowUpDown className="ml-1 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead className="w-[22%]">Due</TableHead>
              <TableHead>
                <Button variant="ghost" size="sm" className="-ml-3" onClick={() => toggleSort('po_difference')}>
                  Linked to <ArrowUpDown className="ml-1 h-3 w-3" />
                </Button>
              </TableHead>
              <TableHead className="w-[180px] text-right">Owed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  Loading invoices...
                </TableCell>
              </TableRow>
            ) : data?.items?.length ? (
              data.items.map((invoice) => (
                <TableRow
                  key={invoice.id}
                  className="cursor-pointer align-top hover:bg-muted/40"
                  data-state={isSelected(invoice) ? 'selected' : undefined}
                  onClick={() => openInvoice(invoice)}
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') openInvoice(invoice)
                  }}
                >
                  <TableCell className="align-top">
                    <BulkSelectCheckbox
                      label={`Select invoice ${invoice.invoice_number || invoice.uuid || invoice.id}`}
                      checked={isSelected(invoice)}
                      onChange={(checked) => setRowSelection(invoice.uuid, checked)}
                    />
                  </TableCell>
                  <TableCell className="min-w-0 align-top">
                    <InvoiceIdentity invoice={invoice} />
                  </TableCell>
                  <TableCell className="min-w-0 space-y-1 align-top">
                    <DueCue invoice={invoice} />
                    <PaymentProgress invoice={invoice} />
                  </TableCell>
                  <TableCell className="min-w-0 align-top">
                    <InvoiceLinks invoice={invoice} />
                  </TableCell>
                  <TableCell className="align-top">
                    <InvoiceOwed invoice={invoice} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No invoices found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-2 lg:hidden">
        {isLoading ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Loading invoices...</p>
        ) : data?.items?.length ? (
          data.items.map((invoice) => (
            <article
              key={invoice.id}
              className="min-w-0 cursor-pointer space-y-2 overflow-hidden rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
              onClick={() => openInvoice(invoice)}
            >
              <div className="flex items-start justify-between gap-3">
                <InvoiceIdentity invoice={invoice} />
                <div className="shrink-0">
                  <InvoiceOwed invoice={invoice} />
                </div>
              </div>
              <div className="space-y-1">
                <DueCue invoice={invoice} />
                <PaymentProgress invoice={invoice} />
              </div>
              <div className="border-t pt-2">
                <InvoiceLinks invoice={invoice} />
              </div>
            </article>
          ))
        ) : (
          <p className="py-4 text-center text-sm text-muted-foreground">No invoices found</p>
        )}
      </div>
    </>
  )
}
